import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import venueApi from '../../lib/venueApi';
import { Card } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Building2, Loader2, ExternalLink } from 'lucide-react';

export default function VenueRequirementsListPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('all');
  const [search, setSearch] = useState('');

  const fetchData = () => {
    setLoading(true);
    const params = {};
    if (status !== 'all') params.status = status;
    if (locationId !== 'all') params.location_id = locationId;
    venueApi
      .get('/requirements', { params })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [status, locationId]);

  useEffect(() => {
    venueApi.get('/locations').then((r) => setLocations(r.data.locations || [])).catch(() => {});
  }, []);

  const filtered = (data?.requirements || []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.company_name.toLowerCase().includes(q) ||
      (r.lab_number || '').toLowerCase().includes(q) ||
      (r.requester_name || '').toLowerCase().includes(q)
    );
  });

  const handleStatusToggle = async (r) => {
    const next = r.status === 'prospect' ? 'confirmed' : 'prospect';
    try {
      await venueApi.patch(`/requirements/${r.id}`, { status: next });
      toast.success(`Marked ${next}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-5" data-testid="venue-list-page">
      <div>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
          <Building2 className="w-5 h-5 text-violet-600" /> All Requirements
        </h2>
        <p className="text-xs text-slate-500 mt-1">Every submitted venue booking, filterable by status and location.</p>
      </div>

      <Card className="p-3 border border-border bg-slate-50/50 flex items-center flex-wrap gap-2">
        <Input
          placeholder="Search company, lab, requester…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-64 text-xs"
          data-testid="venue-list-search"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="venue-list-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="prospect">Prospect</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={locationId} onValueChange={setLocationId}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="venue-list-location"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <span className="text-[11px] text-slate-500">{filtered.length} of {data?.total || 0}</span>
      </Card>

      <Card className="border border-border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-14">No requirements found.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase">Company</TableHead>
                <TableHead className="text-[10px] uppercase">Dates</TableHead>
                <TableHead className="text-[10px] uppercase">Lab · Location</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Strength</TableHead>
                <TableHead className="text-[10px] uppercase">Status</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`venue-list-row-${r.id}`}>
                  <TableCell>
                    <div className="text-sm font-medium text-slate-900">{r.company_name}</div>
                    {r.requester_name && <div className="text-[11px] text-slate-500">by {r.requester_name}</div>}
                  </TableCell>
                  <TableCell className="text-[12px] text-slate-600">
                    {r.from_date}<br />→ {r.to_date}
                  </TableCell>
                  <TableCell className="text-[12px] text-slate-600">
                    <span className="font-medium text-slate-800">Lab {r.lab_number}</span><br />
                    <span className="text-slate-500">{r.location_name}</span>
                  </TableCell>
                  <TableCell className="text-right text-sm">{r.total_strength || '—'}</TableCell>
                  <TableCell>
                    <Badge className={r.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-md" onClick={() => handleStatusToggle(r)} data-testid={`venue-list-toggle-${r.id}`}>
                        {r.status === 'prospect' ? 'Confirm' : 'Prospect'}
                      </Button>
                      <Link to={`/venue/requirements/${r.id}`} data-testid={`venue-list-detail-${r.id}`}>
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] rounded-md text-violet-600 hover:bg-violet-50">
                          <ExternalLink className="w-3 h-3 mr-1" /> Details
                        </Button>
                      </Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
